import { zodResolver } from '@hookform/resolvers/zod';
import { router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { z } from 'zod';

import { ControlledOptionRows, FieldBlock } from '@/components/services/wizard/field';
import { SheetStepFooter, TeamSheet } from '@/components/team/team-sheet';
import { Input } from '@/components/ui/input';
import { useProviderProfile } from '@/hooks/use-provider';
import { useBecomeTeam } from '@/hooks/use-team';
import { useTerms } from '@/hooks/use-terms';
import { showToast } from '@/store/toastStore';
import { MODES_PRESETS, ModesPreset } from '@/types/organization';
import { getApiErrorMessage } from '@/utils/api-error';

const schema = z.object({
  name: z.string().trim().min(2, 'Escreva o nome.').max(80, 'Nome longo demais.'),
  preset: z.custom<ModesPreset>(
    (v) => v === 'SINGLE_TILL' || v === 'EACH_OWN',
    'Escolha um dos dois.',
  ),
});

type Form = z.infer<typeof schema>;

const STEP_FIELDS: Record<1 | 2, (keyof Form)[]> = {
  1: ['name'],
  2: ['preset'],
};

const EMPTY: Partial<Form> = { name: '', preset: undefined };

/**
 * Virar organização: nome → como a equipe trabalha → calculadora do plano de
 * equipe. Nada vem marcado; cada etapa barra o que falta. A organização nasce
 * com o plano pendente: até o pagamento ser confirmado, só os planos abrem
 * (com a saída "cancelar organização" lá).
 */
export function BecomeTeamSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const terms = useTerms();
  const { data: provider } = useProviderProfile();
  const becomeTeam = useBecomeTeam();
  const [step, setStep] = useState<1 | 2>(1);
  const [error, setError] = useState<string | null>(null);

  const { control, trigger, handleSubmit, reset } = useForm<Form>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    reset(EMPTY);
    setStep(1);
    setError(null);
  }, [open, reset]);

  const g = (word: string) => terms.agree(word, 'organization');

  const advance = async () => {
    const ok = await trigger(STEP_FIELDS[step]);
    if (!ok) return;
    setStep(2);
  };

  const onSubmit = async (form: Form) => {
    setError(null);
    try {
      await becomeTeam.mutateAsync({ name: form.name.trim(), ...MODES_PRESETS[form.preset] });
      onClose();
      showToast('Agora escolha o plano da equipe.', 'success');
      // Já com você + 1: quem vira organização é porque vai entrar alguém.
      router.replace({
        pathname: '/subscription/plans',
        params: { professionals: '2' },
      } as never);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível criar a equipe agora.'));
    }
  };

  const busy = becomeTeam.isPending;

  return (
    <TeamSheet
      open={open}
      onClose={onClose}
      busy={busy}
      title="Montar equipe"
      subtitle={`${step} de 2`}
      footer={
        <SheetStepFooter
          onBack={step > 1 ? () => setStep(1) : undefined}
          onNext={step < 2 ? advance : handleSubmit(onSubmit)}
          nextLabel={step < 2 ? 'Continuar' : 'Escolher o plano'}
          busy={busy}
          error={error}
        />
      }
    >
      <View style={{ display: step === 1 ? 'flex' : 'none' }}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <FieldBlock
              title={`Nome ${g('do')} ${terms.organization}`}
              error={fieldState.error?.message}
            >
              <Input
                value={field.value ?? ''}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
                placeholder={provider?.title || `Nome ${g('do')} ${terms.organization}`}
                autoCapitalize="words"
                returnKeyType="next"
                onSubmitEditing={advance}
                style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
              />
            </FieldBlock>
          )}
        />
      </View>

      <View style={{ display: step === 2 ? 'flex' : 'none' }}>
        <ControlledOptionRows<Form, ModesPreset>
          control={control}
          name="preset"
          title="Como sua equipe trabalha?"
          note="Dá para ajustar depois"
          options={[
            {
              value: 'SINGLE_TILL',
              label: 'Caixa único',
              meaning: `O cliente paga ${g('ao')} ${terms.organization}.`,
            },
            {
              value: 'EACH_OWN',
              label: 'Cada um com seu caixa',
              meaning: 'Cada um recebe no próprio Pix.',
            },
          ]}
        />
      </View>
    </TeamSheet>
  );
}
